import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  User, Image, Settings, LogOut, Save, Send, Upload, Music, MapPin, 
  Instagram, Twitter, Youtube, AlertCircle, CheckCircle, Clock, X, Bell, MessageCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { MediaGallery, ImageUrlGallery } from '../../components/MediaGallery';
import { NotificationBell, NotificationsPanel } from '../../components/NotificationBell';
import { UserInbox } from '../../components/Inbox';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sidebar = ({ activeTab, setActiveTab }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const menuItems = [
    { id: 'profile', icon: User, label: t('dashboard.profile') },
    { id: 'media', icon: Image, label: t('dashboard.media') },
    { id: 'messages', icon: MessageCircle, label: 'Mensajes' },
    { id: 'notifications', icon: Bell, label: 'Notificaciones' },
    { id: 'settings', icon: Settings, label: t('dashboard.settings') },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-white/5 flex flex-col z-40">
      <div className="p-6 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="/gs-agency-logo.png" 
            alt="GS Agency" 
            className="h-10 w-auto object-contain"
          />
        </Link>
      </div>

      <nav className="flex-1 p-4 overflow-y-auto">
        <p className="text-white/40 text-xs uppercase tracking-wider px-4 mb-4">Panel de Artista</p>
        <div className="space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                activeTab === item.id
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon size={18} />
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3 px-4 py-3 mb-2">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
              <User size={16} className="text-white/60" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm truncate">{user?.name}</p>
            <p className="text-white/40 text-xs capitalize">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition-colors text-sm"
          data-testid="logout-btn"
        >
          <LogOut size={18} />
          {t('nav.logout')}
        </button>
      </div>
    </aside>
  );
};

const StatusBadge = ({ status }) => {
  const config = {
    draft: { bg: 'bg-white/10', text: 'text-white/60', icon: Clock, label: 'Borrador' },
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock, label: 'Pendiente de Aprobación' },
    approved: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle, label: 'Aprobado y Visible' },
    rejected: { bg: 'bg-red-500/20', text: 'text-red-400', icon: X, label: 'Rechazado' },
    changes_requested: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: AlertCircle, label: 'Cambios Solicitados' },
  };

  const { bg, text, icon: Icon, label } = config[status] || config.draft;

  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${bg} ${text}`}>
      <Icon size={14} />
      {label}
    </span>
  );
};

const ProfileTab = ({ token, user }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: { es: '', en: '' },
    biography: { es: '', en: '' },
    genres: [],
    categories: [],
    city: '',
    country: '',
    format: '',
    social_links: { instagram: '', twitter: '', youtube: '', spotify: '' },
    cover_image: '',
    gallery: [],
    videos: [],
    press_kit_url: '',
    rider_url: '',
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/artist/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          setProfile(response.data);
          setFormData({
            name: response.data.name || { es: user?.name || '', en: user?.name || '' },
            biography: response.data.biography || { es: '', en: '' },
            genres: response.data.genres || [],
            categories: response.data.categories || [],
            city: response.data.city || '',
            country: response.data.country || '',
            format: response.data.format || '',
            social_links: response.data.social_links || {},
            cover_image: response.data.cover_image || '',
            gallery: response.data.gallery || [],
            videos: response.data.videos || [],
            press_kit_url: response.data.press_kit_url || '',
            rider_url: response.data.rider_url || '',
          });
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) {
      fetchProfile();
    }
  }, [token, user]);

  const handleChange = (field, value, subLang = null) => {
    if (subLang) {
      setFormData(prev => ({
        ...prev,
        [field]: { ...prev[field], [subLang]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSocialChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      social_links: { ...prev.social_links, [platform]: value }
    }));
  };

  const handleSave = async (submit = false) => {
    setSaving(true);
    try {
      if (!profile) {
        // Create profile
        await axios.post(`${API}/artist/profile`, {
          ...formData,
          slug: user?.user_id || `artist-${Date.now()}`
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Update profile
        await axios.put(`${API}/artist/profile`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (submit) {
        await axios.post(`${API}/artist/profile/submit`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Perfil enviado para revisión. Te notificaremos cuando sea aprobado.');
      } else {
        toast.success('Perfil guardado como borrador');
      }

      // Refresh profile
      const response = await axios.get(`${API}/artist/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      toast.error('Error al guardar el perfil');
      console.error('Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header with status */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-white mb-2">{t('dashboard.profile')}</h2>
          {profile && <StatusBadge status={profile.status} />}
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="btn-secondary flex items-center gap-2 text-sm px-5 py-2.5"
          >
            <Save size={16} />
            {t('dashboard.save_draft')}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving || profile?.status === 'pending'}
            className="btn-gold flex items-center gap-2 text-sm px-5 py-2.5"
          >
            <Send size={16} />
            {t('dashboard.submit_review')}
          </button>
        </div>
      </div>

      {/* Review notes alert */}
      {profile?.review_notes?.length > 0 && (profile.status === 'changes_requested' || profile.status === 'rejected') && (
        <div className={`rounded-xl p-4 border ${
          profile.status === 'rejected' ? 'bg-red-500/10 border-red-500/30' : 'bg-orange-500/10 border-orange-500/30'
        }`}>
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className={profile.status === 'rejected' ? 'text-red-400' : 'text-orange-400'} />
            <div>
              <h4 className={`font-medium mb-1 ${profile.status === 'rejected' ? 'text-red-400' : 'text-orange-400'}`}>
                {profile.status === 'rejected' ? 'Perfil Rechazado' : 'Cambios Solicitados'}
              </h4>
              <p className="text-white/70 text-sm">
                {profile.review_notes[profile.review_notes.length - 1]?.notes}
              </p>
              <p className="text-white/40 text-xs mt-2">
                Por: {profile.review_notes[profile.review_notes.length - 1]?.reviewer_name} • 
                {new Date(profile.review_notes[profile.review_notes.length - 1]?.timestamp).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Profile visibility info */}
      {profile?.status === 'approved' && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <CheckCircle size={20} className="text-green-400" />
            <div>
              <p className="text-green-400 font-medium">Tu perfil está visible públicamente</p>
              <a 
                href={`/talent/${profile.slug}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-green-400/70 text-sm hover:underline"
              >
                Ver perfil público →
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Basic Info */}
        <div className="glass-dark rounded-xl p-6 space-y-6">
          <h3 className="text-white font-medium flex items-center gap-2">
            <User size={18} className="text-[#D4AF37]" />
            Información Básica
          </h3>
          
          <div>
            <label className="block text-white/60 text-sm mb-2">Nombre Artístico (ES) *</label>
            <Input
              value={formData.name.es}
              onChange={(e) => handleChange('name', e.target.value, 'es')}
              className="input-premium w-full"
              placeholder="Tu nombre artístico"
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Artist Name (EN)</label>
            <Input
              value={formData.name.en}
              onChange={(e) => handleChange('name', e.target.value, 'en')}
              className="input-premium w-full"
              placeholder="Your artist name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-white/60 text-sm mb-2">Ciudad *</label>
              <Input
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className="input-premium w-full"
                placeholder="Ciudad de México"
              />
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-2">País *</label>
              <Input
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
                className="input-premium w-full"
                placeholder="México"
              />
            </div>
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Formato</label>
            <Input
              value={formData.format}
              onChange={(e) => handleChange('format', e.target.value)}
              className="input-premium w-full"
              placeholder="DJ Set, Live, Band, Solo"
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Géneros (separados por coma)</label>
            <Input
              value={formData.genres.join(', ')}
              onChange={(e) => handleChange('genres', e.target.value.split(',').map(g => g.trim()).filter(Boolean))}
              className="input-premium w-full"
              placeholder="House, Techno, Deep House"
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Categorías</label>
            <Input
              value={formData.categories.join(', ')}
              onChange={(e) => handleChange('categories', e.target.value.split(',').map(c => c.trim()).filter(Boolean))}
              className="input-premium w-full"
              placeholder="DJ, Producer, Live Act"
            />
          </div>
        </div>

        {/* Biography */}
        <div className="glass-dark rounded-xl p-6 space-y-6">
          <h3 className="text-white font-medium flex items-center gap-2">
            <Music size={18} className="text-[#D4AF37]" />
            Biografía
          </h3>
          
          <div>
            <label className="block text-white/60 text-sm mb-2">Biografía (ES) *</label>
            <Textarea
              value={formData.biography.es}
              onChange={(e) => handleChange('biography', e.target.value, 'es')}
              className="input-premium w-full resize-none"
              rows={6}
              placeholder="Cuéntanos tu historia, trayectoria y estilo..."
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Biography (EN)</label>
            <Textarea
              value={formData.biography.en}
              onChange={(e) => handleChange('biography', e.target.value, 'en')}
              className="input-premium w-full resize-none"
              rows={6}
              placeholder="Tell us your story, career and style..."
            />
          </div>
        </div>

        {/* Social Links */}
        <div className="glass-dark rounded-xl p-6 space-y-6">
          <h3 className="text-white font-medium">Redes Sociales</h3>
          
          <div className="space-y-4">
            <div className="relative">
              <Instagram size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                value={formData.social_links.instagram || ''}
                onChange={(e) => handleSocialChange('instagram', e.target.value)}
                className="input-premium w-full pl-12"
                placeholder="https://instagram.com/tuusuario"
              />
            </div>
            <div className="relative">
              <Twitter size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                value={formData.social_links.twitter || ''}
                onChange={(e) => handleSocialChange('twitter', e.target.value)}
                className="input-premium w-full pl-12"
                placeholder="https://twitter.com/tuusuario"
              />
            </div>
            <div className="relative">
              <Youtube size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                value={formData.social_links.youtube || ''}
                onChange={(e) => handleSocialChange('youtube', e.target.value)}
                className="input-premium w-full pl-12"
                placeholder="https://youtube.com/@tucanal"
              />
            </div>
          </div>
        </div>

        {/* Files */}
        <div className="glass-dark rounded-xl p-6 space-y-6">
          <h3 className="text-white font-medium">Archivos Promocionales</h3>
          
          <div>
            <label className="block text-white/60 text-sm mb-2">Press Kit (URL)</label>
            <Input
              value={formData.press_kit_url}
              onChange={(e) => handleChange('press_kit_url', e.target.value)}
              className="input-premium w-full"
              placeholder="https://drive.google.com/..."
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Rider Técnico (URL)</label>
            <Input
              value={formData.rider_url}
              onChange={(e) => handleChange('rider_url', e.target.value)}
              className="input-premium w-full"
              placeholder="https://drive.google.com/..."
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Videos (URLs separadas por coma)</label>
            <Input
              value={formData.videos.join(', ')}
              onChange={(e) => handleChange('videos', e.target.value.split(',').map(v => v.trim()).filter(Boolean))}
              className="input-premium w-full"
              placeholder="https://youtube.com/watch?v=..."
            />
          </div>
        </div>

        {/* Cover Image & Gallery */}
        <div className="glass-dark rounded-xl p-6 space-y-6 lg:col-span-2">
          <h3 className="text-white font-medium flex items-center gap-2">
            <Image size={18} className="text-[#D4AF37]" />
            Imágenes
          </h3>

          <div>
            <label className="block text-white/60 text-sm mb-2">Imagen de Portada (URL) *</label>
            <Input
              value={formData.cover_image}
              onChange={(e) => handleChange('cover_image', e.target.value)}
              className="input-premium w-full"
              placeholder="https://..."
            />
            {formData.cover_image && (
              <img src={formData.cover_image} alt="Cover preview" className="mt-3 h-40 object-cover rounded-lg" />
            )}
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Galería de Fotos</label>
            <ImageUrlGallery
              urls={formData.gallery}
              onUrlsChange={(urls) => handleChange('gallery', urls)}
              maxItems={12}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const MediaTab = ({ token }) => {
  const [mediaItems, setMediaItems] = useState([]);

  return (
    <div className="space-y-8">
      <h2 className="font-display text-2xl text-white">Mis Archivos</h2>

      <div className="glass-dark rounded-xl p-8">
        <MediaGallery
          items={mediaItems}
          onItemsChange={setMediaItems}
          token={token}
          folder="artists"
          maxItems={20}
        />
      </div>

      <div className="glass-dark rounded-xl p-6">
        <h3 className="text-white font-medium mb-4">Consejos para tus fotos</h3>
        <ul className="space-y-2 text-white/60 text-sm">
          <li>• Usa fotos de alta calidad (mínimo 1200x1200px)</li>
          <li>• La foto de portada debe ser vertical (3:4) para mejor visualización</li>
          <li>• Incluye fotos en vivo, de estudio y promocionales</li>
          <li>• Evita logos o texto sobre las imágenes</li>
          <li>• Formatos aceptados: JPG, PNG, WEBP (máx 50MB)</li>
        </ul>
      </div>
    </div>
  );
};

const SettingsTab = ({ user }) => {
  return (
    <div className="space-y-8">
      <h2 className="font-display text-2xl text-white">Configuración</h2>

      <div className="glass-dark rounded-xl p-6 space-y-6">
        <h3 className="text-white font-medium">Información de Cuenta</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-white/60 text-sm mb-2">Nombre</label>
            <Input value={user?.name || ''} disabled className="input-premium w-full opacity-60" />
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-2">Email</label>
            <Input value={user?.email || ''} disabled className="input-premium w-full opacity-60" />
          </div>
        </div>

        <p className="text-white/40 text-sm">
          Para cambiar tu información de cuenta, contacta a support@gsagency.com
        </p>
      </div>

      <div className="glass-dark rounded-xl p-6 space-y-4">
        <h3 className="text-white font-medium">Zona de Peligro</h3>
        <p className="text-white/60 text-sm">
          Si deseas eliminar tu cuenta y todos tus datos, contacta al equipo de soporte.
        </p>
      </div>
    </div>
  );
};

export default function ArtistDashboard() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  const renderTab = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab token={token} user={user} />;
      case 'media':
        return <MediaTab token={token} />;
      case 'messages':
        return <UserInbox />;
      case 'notifications':
        return <NotificationsPanel />;
      case 'settings':
        return <SettingsTab user={user} />;
      default:
        return <ProfileTab token={token} user={user} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="artist-dashboard">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      
      <main className="ml-64 p-8">
        <div className="max-w-5xl mx-auto">
          {renderTab()}
        </div>
      </main>
    </div>
  );
}
